import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Filter, Plus, Clock, User, CheckCircle, AlertCircle, DollarSign, Target, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import CreateBountyDialog from '@/components/CreateBountyDialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const CATEGORIES = [
  "All Categories",
  "Restaurants",
  "Retail",
  "Services",
  "Beauty & Wellness",
  "Professional Services",
  "Health & Medical",
  "Entertainment",
  "Automotive",
  "Arts & Culture",
  "Real Estate & Housing",
  "Travel & Lodging",
  "Media & Creative Agencies",
  "Community & Non-Profit",
  "Home & Construction",
  "Manufacturing & Wholesale",
  "Technology",
  "Education",
  "Other"
];

function BountyCard({ bounty, onClaim }) {
  const isExpiringSoon = bounty.expires_at && 
    new Date(bounty.expires_at) - new Date() < 7 * 24 * 60 * 60 * 1000;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl p-6 hover:border-zinc-700 transition-colors"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {bounty.category && (
              <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 text-xs">
                {bounty.category}
              </span>
            )}
            {isExpiringSoon && (
              <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Expiring soon
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-white mb-1">{bounty.title}</h3>
          {(bounty.city || bounty.state) && (
            <div className="flex items-center gap-1 text-zinc-400 text-sm">
              <MapPin className="w-3.5 h-3.5" />
              <span>
                {[bounty.city, bounty.state].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
        
        {/* Reward Badge */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
          <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
          <span className="text-lg font-bold text-green-400">{bounty.reward_amount}</span>
          <span className="text-green-400/70 text-sm">MEL</span>
        </div>
      </div>

      {/* Description */}
      {bounty.description && (
        <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
          {bounty.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-3">
          {bounty.creator_picture ? (
            <img 
              src={bounty.creator_picture} 
              alt={bounty.creator_name}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center">
              <User className="w-3 h-3 text-zinc-500" />
            </div>
          )}
          <span className="text-zinc-500 text-sm">by {bounty.creator_name}</span>
          {bounty.active_claims > 0 && (
            <span className="text-zinc-500 text-xs">
              • {bounty.active_claims} claim{bounty.active_claims > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <Link
          to={`/bounties/${bounty.bounty_id}`}
          className="flex items-center gap-1 text-sm text-white hover:text-green-400 transition-colors"
          data-testid={`view-bounty-${bounty.bounty_id}`}
        >
          View Details
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Approval Badge */}
      {bounty.requires_approval && (
        <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
          <AlertCircle className="w-3 h-3" />
          Requires creator approval
        </div>
      )}
    </motion.div>
  );
}

export default function GigBoardPage() {
  const [bounties, setBounties] = useState([]);
  const [featuredBounties, setFeaturedBounties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    fetchBounties();
    fetchFeaturedBounties();
    fetchCategories();
    checkAuth();
  }, [selectedCategory, cityFilter, stateFilter]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/bounties/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFeaturedBounties = async () => {
    try {
      const response = await fetch(`${API}/bounties/featured?limit=4`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedBounties(data);
      }
    } catch (error) {
      console.error('Error fetching featured bounties:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Fetch wallet balance
        const walletRes = await fetch(`${API}/wallet`, {
          credentials: 'include'
        });
        if (walletRes.ok) {
          const walletData = await walletRes.json();
          setWalletBalance(walletData.balance);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    }
  };

  const fetchBounties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'All Categories') {
        params.append('category', selectedCategory);
      }
      if (cityFilter) {
        params.append('city', cityFilter);
      }
      if (stateFilter) {
        params.append('state', stateFilter);
      }

      const response = await fetch(`${API}/bounties?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setBounties(data.bounties);
      }
    } catch (error) {
      console.error('Error fetching bounties:', error);
      toast.error('Failed to load bounties');
    } finally {
      setLoading(false);
    }
  };

  const handleBountyCreated = () => {
    fetchBounties();
    checkAuth(); // Refresh wallet balance
  };

  const filteredBounties = bounties.filter(bounty => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      bounty.title.toLowerCase().includes(query) ||
      bounty.description?.toLowerCase().includes(query) ||
      bounty.category?.toLowerCase().includes(query) ||
      bounty.city?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Community
              </p>
              <h1 className="text-3xl font-bold text-white tracking-tight">
                Bounty Board
              </h1>
              <p className="text-zinc-400 mt-2">
                Find businesses the community needs and earn MEL rewards
              </p>
            </div>

            <button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to create a bounty');
                  return;
                }
                setCreateDialogOpen(true);
              }}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="create-bounty-btn"
            >
              <Plus className="w-4 h-4" />
              Create Bounty
            </button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card rounded-xl p-4">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Active Bounties</p>
              <p className="text-2xl font-bold text-white">{bounties.length}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Total Rewards</p>
              <div className="flex items-center gap-2">
                <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
                <p className="text-2xl font-bold text-green-400">
                  {bounties.reduce((sum, b) => sum + b.reward_amount, 0).toFixed(0)}
                </p>
                <span className="text-zinc-500">MEL</span>
              </div>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Categories</p>
              <p className="text-2xl font-bold text-white">
                {new Set(bounties.map(b => b.category).filter(Boolean)).size}
              </p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Locations</p>
              <p className="text-2xl font-bold text-white">
                {new Set(bounties.map(b => b.city).filter(Boolean)).size}
              </p>
            </div>
          </div>

          {/* Featured Bounties */}
          {featuredBounties.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-green-400" />
                <h2 className="font-semibold text-white">Featured Bounties</h2>
                <span className="text-zinc-500 text-sm">Highest Rewards</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {featuredBounties.map((bounty, index) => (
                  <Link
                    key={bounty.bounty_id}
                    to={`/bounties/${bounty.bounty_id}`}
                    className="block p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 hover:border-green-500/40 transition-colors"
                    data-testid={`featured-bounty-${bounty.bounty_id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
                      <span className="text-green-400 font-bold text-lg">{bounty.reward_amount}</span>
                      <span className="text-green-400/70 text-sm">MEL</span>
                    </div>
                    <h3 className="text-white font-medium text-sm line-clamp-2 mb-1">{bounty.title}</h3>
                    {bounty.category && (
                      <p className="text-zinc-500 text-xs">{bounty.category}</p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Category Tags */}
          {categories.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-4 h-4 text-zinc-500" />
                <h3 className="text-sm text-zinc-400">Quick Categories</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('All Categories')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedCategory === 'All Categories'
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                  }`}
                  data-testid="category-tag-all"
                >
                  All
                </button>
                {categories.filter(c => c.count > 0).map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.label.split(' ')[0])}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1 ${
                      selectedCategory.toLowerCase().includes(cat.id)
                        ? 'bg-white text-black'
                        : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                    }`}
                    data-testid={`category-tag-${cat.id}`}
                  >
                    {cat.label.split(' ')[0]}
                    <span className="text-xs opacity-70">({cat.count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass-card rounded-xl p-4 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative md:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search bounties..."
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="bounty-search-input"
                />
              </div>

              {/* Category */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-zinc-800">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-800">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* City */}
              <Input
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="City"
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                data-testid="city-filter"
              />

              {/* State */}
              <Input
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
                placeholder="State (e.g., GA)"
                maxLength={2}
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                data-testid="state-filter"
              />
            </div>
          </div>

          {/* Bounties List */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-40 skeleton rounded-xl" />
              ))}
            </div>
          ) : filteredBounties.length > 0 ? (
            <div className="space-y-4">
              {filteredBounties.map((bounty, index) => (
                <BountyCard 
                  key={bounty.bounty_id} 
                  bounty={bounty}
                />
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-xl p-12 text-center">
              <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Bounties Found</h3>
              <p className="text-zinc-500 mb-6">
                {searchQuery || selectedCategory !== 'All Categories' || cityFilter || stateFilter
                  ? 'Try adjusting your filters to find more bounties.'
                  : 'Be the first to create a bounty and help grow the community!'}
              </p>
              {user && (
                <button
                  onClick={() => setCreateDialogOpen(true)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create First Bounty
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create Bounty Dialog */}
      <CreateBountyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        walletBalance={walletBalance}
        onBountyCreated={handleBountyCreated}
      />
    </div>
  );
}
